import type { CameraView, SceneDocument } from "./types";

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const assertUnit = (value: number, name: string): void => {
  if (!isFiniteNumber(value) || value < 0 || value > 1) {
    throw new Error(`${name} must be between 0 and 1.`);
  }
};

const assertCamera = (camera: CameraView, name: string): void => {
  if (
    !isFiniteNumber(camera.center.x) ||
    !isFiniteNumber(camera.center.y) ||
    !isFiniteNumber(camera.zoom) ||
    camera.zoom <= 0
  ) {
    throw new Error(`${name} contains an invalid camera value.`);
  }
};

export const validateScene = (document: SceneDocument): SceneDocument => {
  if (document.schemaVersion !== 1) throw new Error("Unsupported scene schema.");
  if (!document.id.trim()) throw new Error("Scene ID is required.");
  if (!isFiniteNumber(document.artboard.width) || document.artboard.width <= 0) {
    throw new Error("Artboard width must be positive.");
  }
  if (!isFiniteNumber(document.artboard.height) || document.artboard.height <= 0) {
    throw new Error("Artboard height must be positive.");
  }
  if (document.chapters.length !== 3) throw new Error("The scene must have exactly three chapters.");

  assertCamera(document.initialCamera.desktop, "Initial desktop camera");
  assertCamera(document.initialCamera.mobile, "Initial mobile camera");

  const ids = new Set<string>();
  const groups = new Map<string, number>();
  for (const layer of document.layers) {
    if (!layer.id.trim() || ids.has(layer.id)) throw new Error(`Duplicate or empty layer ID: ${layer.id}`);
    ids.add(layer.id);
    if (
      !isFiniteNumber(layer.bounds.x) ||
      !isFiniteNumber(layer.bounds.y) ||
      !isFiniteNumber(layer.bounds.width) ||
      !isFiniteNumber(layer.bounds.height) ||
      layer.bounds.width <= 0 ||
      layer.bounds.height <= 0
    ) {
      throw new Error(`Layer ${layer.id} has invalid bounds.`);
    }
    if (!isFiniteNumber(layer.z)) throw new Error(`Layer ${layer.id} has an invalid z value.`);
    assertUnit(layer.depth, `${layer.id}.depth`);
    assertUnit(layer.opacity, `${layer.id}.opacity`);
    assertUnit(layer.revealAt, `${layer.id}.revealAt`);
    const knownDepth = groups.get(layer.motionGroup);
    if (knownDepth !== undefined && knownDepth !== layer.depth) {
      throw new Error(`Motion group ${layer.motionGroup} has inconsistent depths.`);
    }
    groups.set(layer.motionGroup, layer.depth);
  }
  if (!document.layers.some((layer) => layer.required && layer.revealAt === 0)) {
    throw new Error("A required base layer must remain at assembly progress zero.");
  }

  const chapterIds = new Set<string>();
  for (const chapter of document.chapters) {
    if (!chapter.id.trim() || chapterIds.has(chapter.id)) throw new Error(`Duplicate chapter ID: ${chapter.id}`);
    chapterIds.add(chapter.id);
    assertCamera(chapter.camera.desktop, `${chapter.id} desktop camera`);
    assertCamera(chapter.camera.mobile, `${chapter.id} mobile camera`);
    for (const [layerId, opacity] of Object.entries(chapter.emphasis ?? {})) {
      if (!ids.has(layerId)) throw new Error(`Chapter ${chapter.id} references unknown layer ${layerId}.`);
      assertUnit(opacity, `${chapter.id}.${layerId} emphasis`);
    }
  }

  const hotspotIds = new Set<string>();
  for (const hotspot of document.hotspots) {
    if (!hotspot.id.trim() || hotspotIds.has(hotspot.id)) throw new Error(`Duplicate hotspot ID: ${hotspot.id}`);
    hotspotIds.add(hotspot.id);
    if (!ids.has(hotspot.layerId)) throw new Error(`Hotspot ${hotspot.id} references an unknown layer.`);
    if (!chapterIds.has(hotspot.chapterId)) throw new Error(`Hotspot ${hotspot.id} references an unknown chapter.`);
    if (!isFiniteNumber(hotspot.point.x) || !isFiniteNumber(hotspot.point.y)) {
      throw new Error(`Hotspot ${hotspot.id} has invalid coordinates.`);
    }
  }

  return document;
};
